import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CleaningDetailService from '../../services/cleaningDetail.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const CleaningDetailList = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdFilter = searchParams.get('clientId') || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (clientIdFilter) params.client_id = clientIdFilter;
      const res = await CleaningDetailService.getAll(params);
      setRows(res.data?.data || []);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningDetails.loadError'),
        severity: 'error',
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientIdFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const clearClientFilter = () => {
    searchParams.delete('clientId');
    setSearchParams(searchParams, { replace: true });
  };

  const confirmDelete = async () => {
    try {
      await CleaningDetailService.delete(deleteId);
      setSnackbar({ open: true, message: t('cleaningDetails.deletedSuccess'), severity: 'success' });
      setDeleteId(null);
      load();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningDetails.deleteError'),
        severity: 'error',
      });
    }
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
            <Typography variant="h5">{t('cleaningDetails.title')}</Typography>
            {hasPermission('cleaning-details.create') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate(
                    clientIdFilter
                      ? `/cleaning-details/new?clientId=${encodeURIComponent(clientIdFilter)}`
                      : '/cleaning-details/new'
                  )
                }
              >
                {t('cleaningDetails.new')}
              </Button>
            )}
          </Box>
          {clientIdFilter && (
            <Chip
              label={t('cleaningDetails.filteredByClient')}
              onDelete={clearClientFilter}
              sx={{ mb: 2 }}
              color="primary"
              variant="outlined"
            />
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('cleaningDetails.client')}</TableCell>
                  <TableCell>{t('cleaningDetails.schedule')}</TableCell>
                  <TableCell align="right">{t('cleaningDetails.horas')}</TableCell>
                  <TableCell align="right">{t('cleaningDetails.hourlyRate')}</TableCell>
                  <TableCell align="right">{t('cleaningDetails.income')}</TableCell>
                  <TableCell align="right">{t('cleaningDetails.wages')}</TableCell>
                  <TableCell align="center">{t('cleaningDetails.team')}</TableCell>
                  <TableCell align="center">{t('cleaningDetails.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t('cleaningDetails.none')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.client?.name || '—'}</TableCell>
                      <TableCell>
                        {row.recurrence
                          ? (() => {
                              const time = String(row.recurrence.start_time || '').slice(0, 5);
                              if (row.recurrence.schedule === 'every_n_weeks') {
                                return `${t('cleaningDetails.scheduleEveryNWeeks', {
                                  n: row.recurrence.every_weeks ?? '?',
                                })} · ${time}`;
                              }
                              const kind = {
                                daily: t('cleaningDetails.daily'),
                                weekly: t('cleaningDetails.weekly'),
                                monthly: t('cleaningDetails.monthly'),
                              }[row.recurrence.schedule];
                              return `${kind || row.recurrence.schedule} · ${time}`;
                            })()
                          : t('cleaningDetails.onDemandNoSchedule')}
                      </TableCell>
                      <TableCell align="right">{row.horas != null ? Number(row.horas).toFixed(2) : '—'}</TableCell>
                      <TableCell align="right">
                        {row.hourly_rate != null ? Number(row.hourly_rate).toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="right">{row.income != null ? Number(row.income).toFixed(2) : '—'}</TableCell>
                      <TableCell align="right">{row.wages != null ? Number(row.wages).toFixed(2) : '—'}</TableCell>
                      <TableCell align="center">{row.cleaners?.length ?? 0}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/cleaning-details/${row.id}/edit`)}
                          disabled={!hasPermission('cleaning-details.edit')}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteId(row.id)}
                          disabled={!hasPermission('cleaning-details.delete')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>{t('cleaningDetails.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('cleaningDetails.deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CleaningDetailList;
